import React from 'react';

import { faHouse } from '@fortawesome/pro-regular-svg-icons';
import { Checkbox, FormControl, FormControlLabel, InputLabel, MenuItem, Select } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import cx from 'clsx';
import { filter, find, flow, get, isEmpty, map, round, startCase, xorBy } from 'lodash/fp';
import slugify from 'slugify';

import AutocompleteSearch from '../components/AutocompleteSearch';
import StrongTextField from '../components/StrongTextField';
import axios from '../modules/axios';
import LotProgressStatus from '../pages/lot/LotProgressStatus';
import PrixEditionRapide from '../pages/lot/PrixEditionRapide';
import { calculPrixLot } from '../utils';
import { LOTS_STATUS } from '../utils/lots';
import moduleTvas from '../utils/tvas';

const useStyles = makeStyles({
  root: {
    fontWeight: 'bold',
    textTransform: 'capitalize',
  },
  preresa: {
    color: 'rgb(242, 119, 119)',
  },
  option: {
    color: 'rgb(246, 229, 83)',
  },
  enpreparation: {
    color: '#d67b2c',
  },
  envente: {
    color: '#43a048',
  },
  vendu: {
    color: 'rgb(225, 46, 46)',
  },
  reserve: {
    color: 'rgb(225, 46, 46)',
  },
  rdvsignature: {
    color: 'rgb(225, 46, 46)',
  },
});

const StyledSpan = ({ children }) => {
  const classes = useStyles();

  return (
    <span className={cx(classes.root, classes[slugify(children, { strict: true, replacement: '' })])}>{children}</span>
  );
};

const getInfoFor =
  (key, type, suffix = '') =>
  ({ tvalots }) => {
    if (isEmpty(tvalots)) {
      return '-';
    }
    const tvalot = find(({ tva_lot_type }) => tva_lot_type === type, tvalots);

    return tvalot ? `${round(get(key, tvalot))}${suffix}` : '-';
  };

export default {
  name: 'lots',
  title: 'Lots',
  resource: 'lot',
  primaryKey: 'id',
  icon: faHouse,
  query: {
    _associations: 'commercial,tvalots,prog',
    'tvalots._associations': 'tva_taux',
    _fields: 'id,idprog,visible,idcommercialisateur,ref,type,surf_hab,avancement_dossier',
    'tvalots._fields':
      'tva_lot_id,tva_lot_idlot,tva_lot_tva_taux_id,tva_lot_type,tva_lot_price,tva_lot_px_total_ht,tva_lot_taux_commission',
    'tvalots.tva_taux._fields': 'tva_taux_id,tva_taux_name,tva_taux_value',
  },
  sort: 'ref',
  limit: 20,
  head: {
    visible: 'En ligne',
    avancement_dossier: 'État',
    ref: 'Lot',
    type: 'Type',
    surf_hab: 'Surface',
    px_total_vente_acc: 'Prix TTC (Acc)',
    lot_px_ht_acc: 'Prix HT (Acc)',
    lot_tva_acc: 'TVA (Acc)',
    px_total_vente_inv: 'Prix TTC (Inv)',
    lot_px_ht_inv: 'Prix HT (Inv)',
    lot_tva_inv: 'TVA (Inv)',
  },
  list: ({ values }) => [
    <Checkbox checked={values.visible} disabled />,
    <StyledSpan>{values.avancement_dossier}</StyledSpan>,
    values.ref,
    values.type,
    values.surf_hab,
    getInfoFor('tva_lot_price', 'acc'),
    getInfoFor('tva_lot_px_total_ht', 'acc'),
    getInfoFor('tva_taux.tva_taux_value', 'acc', '%'),
    getInfoFor('tva_lot_price', 'inv'),
    getInfoFor('tva_lot_px_total_ht', 'inv'),
    getInfoFor('tva_taux.tva_taux_value', 'inv', '%'),
  ],
  filter: ({ values, props, setFieldValue, handleSubmit, filters }) => [
    <StrongTextField name='ref' label='Ref' values={values.ref} {...props} />,
    <StrongTextField name='type' label='Type' values={values.type} {...props} />,
    filters?.idprog ? null : (
      <AutocompleteSearch
        {...props}
        resource='prog'
        attributeId='id'
        size='small'
        attribute='nom'
        label='Programme'
        filters={{
          _fields: 'id,nom',
        }}
        onChange={(event, value) => {
          setFieldValue('idprog', value?.id);
          handleSubmit();
        }}
      />
    ),
    <FormControl {...props}>
      <InputLabel>
        <strong>Statut</strong>
      </InputLabel>
      <Select
        {...props}
        label='Statut'
        name='avancement_dossier'
        value={values.avancement_dossier}
        displayEmpty
        onChange={e => {
          setFieldValue('avancement_dossier', e.target.value);
          handleSubmit();
        }}
      >
        <MenuItem value={null}>Tous</MenuItem>
        {LOTS_STATUS.map(status => (
          <MenuItem value={status}>{startCase(status)}</MenuItem>
        ))}
      </Select>
    </FormControl>,
  ],
  edit: ({ values, props, handleChange }) => [
    <FormControlLabel
      control={<Checkbox name='visible' checked={values.visible} onChange={handleChange} />}
      label='En ligne'
    />,
    <LotProgressStatus {...props} />,
    <PrixEditionRapide values={values} handleChange={handleChange} />,
  ],
  customUpdate: async ({ newValues: { tvalots, ...lot }, initialValues }) => {
    try {
      // Update the lot
      await axios.patch(`lot/${lot.id}`, lot);

      // Update associated TVA lots
      var tvas = await moduleTvas.get();

      // First the one that have been updated
      await Promise.all(
        flow(
          // Those with an id have been updated
          filter('tva_lot_id'),
          map(async tvalot => {
            // Computed attributes
            const { prixTTC, prixHT, commissionTTC, commissionHT, prixPromoteur } = calculPrixLot(
              parseInt(tvalot.tva_lot_price, 10),
              tvas.find(({ tva_taux_id }) => tva_taux_id === tvalot.tva_lot_tva_taux_id).tva_taux_value,
              tvalot.tva_lot_taux_commission,
              lot.prog.taux_tva,
            );

            const newValue = {
              ...tvalot,
              tva_lot_px_pierre: prixTTC,
              tva_lot_px_total_ht: prixHT,
              tva_lot_price_promoteur: prixPromoteur,
              tva_lot_commission_ttc: commissionTTC,
              tva_lot_commission_ht: commissionHT,
            };

            return axios.patch(`tva_lot/${tvalot.tva_lot_id}`, newValue);
          }),
        )(tvalots),
      );

      // Then add the new one
      await Promise.all(
        flow(
          // Those with an id have been updated
          filter({ tva_lot_id: null }),
          map(async tvalot => {
            // Computed attributes
            const { prixTTC, prixHT, commissionTTC, commissionHT, prixPromoteur } = calculPrixLot(
              parseInt(tvalot.tva_lot_price, 10),
              tvas.find(({ tva_taux_id }) => tva_taux_id === tvalot.tva_lot_tva_taux_id).tva_taux_value,
              tvalot.tva_lot_taux_commission,
              lot.prog.taux_tva,
            );
            const newValue = {
              ...tvalot,
              tva_lot_px_pierre: prixTTC,
              tva_lot_px_total_ht: prixHT,
              tva_lot_price_promoteur: prixPromoteur,
              tva_lot_commission_ttc: commissionTTC,
              tva_lot_commission_ht: commissionHT,
            };

            return axios.post(`tva_lot`, newValue);
          }),
        )(tvalots),
      );

      // Finally remove the deleted one
      const tvaLotIdToDelete = filter(
        Number,
        map('tva_lot_id', xorBy(get('tva_lot_id'), tvalots, initialValues.tvalots)),
      );

      await Promise.all(map(id => axios.delete(`tva_lot/${id}`), tvaLotIdToDelete));
    } catch (error) {
      console.error(error);
    }
  },
  show: true,
  remove: true,
  popup: true,
  addShow: true,
};
